import { ComponentBase } from 'utils/abstract-classes/component-base'
import { CarComparisonStore } from '../store'
import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType } from 'constants/enums'
import { HeaderOffsetService } from 'services/header/header-offset.service'
import { isMobile } from 'utils/system/device'
import { getResultsUrlPath, isMyPicksPage } from '../util'
import { MAX_VEHICLES } from './constants'

const MOBILE_VEHICLE_FINDER_HEIGHT = 46
const SAFARI_TIMEOUT = 500
const { driving: attrName } = ATTR_NAME

export class ComparisonResults extends ComponentBase {
    public readonly headerOffSrv: HeaderOffsetService
    public static pageUrl = '/comparison-results'
    private scrollPanels: HTMLDivElement[]
    private compareGridHeader: HTMLDivElement
    private sentinel: HTMLDivElement
    private menuAnimationPrevState: boolean
    private firstApplication = false
    private stickyTriggerSize: number

    public constructor() {
        super()

        const vehicles = CarComparisonStore.getInstance().get()
        const myPicksPageURL = getResultsUrlPath(
            vehicles.map((vehicle) => vehicle.item)
        )
        const url: URL = new URL(window.location.href)

        // blank comparison results will reload to my picks page from the session if there is no parameter "remove"
        if (
            !url.searchParams.get('remove') &&
            url.pathname === `${ComparisonResults.pageUrl}/` &&
            myPicksPageURL !== `${ComparisonResults.pageUrl}/`
        ) {
            window.location.assign(myPicksPageURL)
        }

        if (!this.initHTMLElements()) {
            return
        }

        this.scrollPanels.forEach((elem: HTMLDivElement, idx: number) => {
            elem.addEventListener(EventType.SCROLL, () => {
                this.moveScrollPanels(elem.scrollLeft)
                // emit from single listener
                if (idx === 0) {
                    this.scrollStickyHorizontally(elem.scrollLeft)
                }
            })
        })

        this.headerOffSrv = new HeaderOffsetService(this.compareGridHeader)
        if (isMobile()) {
            const minHeaderHeight =
                this.headerOffSrv.totalHeaderHeight -
                MOBILE_VEHICLE_FINDER_HEIGHT
            this.stickyTriggerSize =
                minHeaderHeight <= 0
                    ? MOBILE_VEHICLE_FINDER_HEIGHT
                    : minHeaderHeight
            this.observeMenuAnimation()
        } else {
            this.stickyTriggerSize = this.headerOffSrv.totalHeaderHeight
        }
        this.observeCompareGridHeader()

        if (isMyPicksPage() && vehicles.length < MAX_VEHICLES) {
            this.showAddVehicleBtn()
        }
    }

    /**
     * Footer Intersection Observer Callback
     * when footer starts pushing on sticky comparison header
     * on small height viewport
     */
    private releaseSticky(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                this.compareGridHeader.classList.add(
                    CLASS_NAME.comparisonResults
                        .compareGridHeaderStickyFooterPush
                )
            } else {
                this.compareGridHeader.classList.remove(
                    CLASS_NAME.comparisonResults
                        .compareGridHeaderStickyFooterPush
                )
            }
        }
    }

    /**
     * Menu Mutation Observer Callback
     * when menu hide/show on small height viewport
     * 1.reactively reposition sticky
     * 2.start observing footer (we need settled DOM sizing of elements to set correct trigering line )
     *
     */
    private menuAnimationReactionCallback(
        mutationList: MutationRecord[]
    ): void {
        for (const mutation of mutationList) {
            if (mutation.attributeName === 'class') {
                const menuHidden = (
                    mutation.target as HTMLDivElement
                ).classList.contains(CLASS_NAME.header.shrink)
                if (this.menuAnimationPrevState !== menuHidden) {
                    this.menuAnimationPrevState = menuHidden
                    if (menuHidden && !this.firstApplication) {
                        this.firstApplication = true
                        // Timeout needed for Safari iOs
                        // for DOM sizing values not to be null
                        setTimeout(() => {
                            this.observeFooter()
                        }, SAFARI_TIMEOUT)
                    } else {
                        if (menuHidden) {
                            this.setStickyOffset(
                                this.getStickyCurrentOffset() -
                                    MOBILE_VEHICLE_FINDER_HEIGHT
                            )
                        } else {
                            this.setStickyOffset(
                                this.getStickyCurrentOffset() +
                                    MOBILE_VEHICLE_FINDER_HEIGHT
                            )
                        }
                    }
                }
            }
        }
    }

    /**
     * Mutation Observer Setup
     * react to scroll.ts Menu Animation quicker
     * to adjust sticky top position
     * rather than emit Custom Event from scroll.ts
     */
    private observeMenuAnimation(): void {
        this.menuAnimationPrevState = this.compareGridHeader.classList.contains(
            CLASS_NAME.header.shrink
        )
        const mutationObserver = new MutationObserver(
            this.menuAnimationReactionCallback.bind(this)
        )
        mutationObserver.observe(document.documentElement, {
            attributes: true
        })
    }

    /**
     * Sentinel Intersection Observer Callback
     * positions sticky comparison header
     */
    private setSticky(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                this.compareGridHeader.classList.remove(
                    CLASS_NAME.comparisonResults.compareGridHeaderSticky
                )
            } else {
                this.setStickyOffset(this.stickyTriggerSize)
                this.compareGridHeader.classList.add(
                    CLASS_NAME.comparisonResults.compareGridHeaderSticky
                )
            }
        }
    }

    /**
     * move sticky comparison header left and right in sync
     *
     * @param {pos} scroll distances from left and right
     */
    private scrollStickyHorizontally(pos: number): boolean {
        this.compareGridHeader.style.setProperty('left', `-${pos}px`)
        return true
    }

    /**
     * Initialize HTML DOMs
     *
     * @returns {boolean} indicates mandatory DOMs exist
     */
    private initHTMLElements(): boolean {
        const compareGridHeader = this.getElement<HTMLDivElement>(
            `.${CLASS_NAME.comparisonResults.compareGridHeader}`
        )
        const scrollPanels = this.getElements<HTMLDivElement>(
            `[${attrName.comparisonResults.scrollPanel}]`
        )
        const sentinel = this.getElement<HTMLDivElement>('.page-title-banner')

        if (!scrollPanels || !compareGridHeader || !sentinel) {
            return false
        }

        this.scrollPanels = scrollPanels
        this.compareGridHeader = compareGridHeader
        this.sentinel = sentinel

        return true
    }

    /**
     * move scrollPanels left and right in sync
     *
     * @param {pos} scroll distances from left and right
     */
    private moveScrollPanels(pos: number): void {
        this.scrollPanels.forEach((item) => {
            item.scrollTo(pos, 0)
        })
    }

    /**
     * Getting top sticky offset from inline style
     *
     * @returns {number} pixels
     */
    private getStickyCurrentOffset(): number {
        return Number(
            this.compareGridHeader.style
                .getPropertyValue('--offset')
                .split('px')[0]
        )
    }

    /**
     * Setting inline top sticky offset
     *
     * @param {val} top offset
     */
    private setStickyOffset(val: number): void {
        this.compareGridHeader.style.setProperty('--offset', `${val}px`)
    }

    /**
     * Footer Intersection Observer Setup
     * collect height data to set root margin to trigger change
     */
    private observeFooter(): void {
        const triggerLine =
            window.innerHeight -
            Math.round(
                Number(
                    this.compareGridHeader.getBoundingClientRect().bottom +
                        MOBILE_VEHICLE_FINDER_HEIGHT
                )
            )
        new IntersectionObserver(this.releaseSticky.bind(this), {
            rootMargin: `0px 0px -${triggerLine}px 0px`
        }).observe(this.getElement<HTMLDivElement>(`.${CLASS_NAME.footer._}`))
    }

    /**
     * Sticky Intersection Observer Setup to track compare grid header position
     * collect height data to set root margin to trigger change
     */
    private observeCompareGridHeader(): void {
        new IntersectionObserver(this.setSticky.bind(this), {
            rootMargin: `-${this.stickyTriggerSize}px 0px 0px 0px`
        }).observe(this.sentinel)
    }

    /**
     * Display Add a vehicle slot
     * increase overall width of dependent panels
     */
    private showAddVehicleBtn(): void {
        const slots =
            this.scrollPanels[0].style.getPropertyValue('--slots-total')
        this.scrollPanels.forEach((elem: HTMLDivElement) => {
            elem.style.setProperty(
                '--slots-total',
                (Number(slots) + 1).toString()
            )
        })
        this.getElement<HTMLDivElement>(
            `.${CLASS_NAME.comparisonResults.compareGridHeaderAdd}`
        ).hidden = false
    }
}
